import { useEffect, useMemo, useState } from 'react';
import { ago, fmtDate, shortDigest, flyApp, get, post } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { Block, Cell, Grid, Kpis, Kv, List, Pill, SEVERITY, Table, type Row, type Severity, type Tone } from '../ui/Block';
import { Head, useAdmin, type AttentionItem } from '../ui/Shell';
import { JobBanner, pl, stale, type AppKey, type EngineJob, type MachineList } from './machines';

interface AppSummary { name: string; machines: number; started: number; stopped: number; suspended: number; images: string[]; behind: string[]; waitingToUpdate: string[]; genoa: number; ageS: number }
interface AppError { name: string; error: string }
interface Queue { open: number; failed: number; running: number }
interface DbSnapshot { liveRecordings: number; openRecordings: number; listeners: number; queues: Record<string, Queue>; schema?: string; newestMigration?: string; users: number; broadcasts: number; transcriptions: number; recordings: number; artifactsBytes: number }
interface Metrics { publishFailures: number; translationErrors: number; provider429s: number; subscribers: number; apiInstances: number }
interface StaffAction { id: string; action: string; actor: string; at: string; targetType: string; targetId: string }
export interface Overview {
  env: string;
  db: { value: DbSnapshot | null; ageS: number; asleep: boolean };
  apps: Partial<Record<AppKey, AppSummary | AppError>>;
  flyError: string | null;
  metrics: Metrics | null;
  usagePending: number;
  pool: { size: number; idle: number; max: string | null };
  awake: boolean;
  listeners: number;
  config: { openai: boolean; workos: boolean; apns: boolean; fcm: boolean; fly: boolean; github: boolean; engine: string | null; translationProvider: string; engineConcurrency: string | null; webappOrigins: string[] };
  image: string | null;
  staffActions: StaffAction[];
}

const isErr = (a?: AppSummary | AppError): a is AppError => !!a && 'error' in a;
const ROLE: Record<AppKey, string> = { engine: 'live and file transcription', 'engine-en': 'English transcription (Parakeet)', api: 'auth, data, fan-out', app: 'web app' };
interface Dismissed { id: string; title: string; by: string; on: string; until: string | null }
const SNOOZE: [string, number][] = [['1 day', 24], ['1 week', 24 * 7], ['30 days', 24 * 30]];
const QUEUES: [string, string, string][] = [['finalize_jobs', 'FINALIZE', 'finalize'], ['transcription_jobs', 'TRANSCRIPTIONS', 'transcription'], ['stream_ingests', 'STREAMS', 'ingest'], ['postprocess_jobs', 'POSTPROCESS', 'postprocess'], ['voice_jobs', 'VOICE', 'voice'], ['voice_train_jobs', 'VOICE TRAINING', 'voice_training'], ['push_outbox', 'PUSH', 'push'], ['idp_deletions', 'IDP DELETIONS', 'idp']];

export function Overview() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const [wake, setWake] = useState(false);
  const ov = useData<Overview>(env, `/admin/overview?wake=${wake}`, 5, paused);
  const jobs = useData<{ jobs: EngineJob[] }>(env, '/admin/engines/jobs', 15, paused);
  const engines = useData<MachineList>(env, '/admin/machines?app=engine', 15, paused);
  const [sev, setSev] = useState<Severity | ''>('');
  const [gone, setGone] = useState<Set<string>>(new Set());
  const [snoozing, setSnoozing] = useState<string | null>(null);
  const [hiddenList, setHiddenList] = useState<Dismissed[] | null>(null);
  const showHidden = () => get<{ dismissed: Dismissed[] }>(env, '/admin/attention/dismissed').then((r) => setHiddenList(r.dismissed)).catch((e) => ctx.toast((e as Error).message, '', { logged: false }));
  const restore = async (id: string) => {
    try { await post(env, '/admin/attention/restore', { id }); setGone((g) => { const n = new Set(g); n.delete(id); return n; }); ctx.reloadAttention(); if (hiddenList) void showHidden(); } catch (e) { ctx.toast((e as Error).message, '', { logged: false }); }
  };
  const dismiss = async (it: AttentionItem, hours?: number) => {
    setSnoozing(null);
    setGone((g) => new Set(g).add(it.id));
    try {
      await post(env, '/admin/attention/dismiss', { id: it.id, snoozeHours: hours });
      const marksRead = !hours && it.kind === 'notification';
      ctx.toast(hours ? `Snoozed for ${SNOOZE.find(([, h]) => h === hours)?.[0]}` : marksRead ? 'Marked read' : 'Done', marksRead ? it.title : hours ? it.title : 'Hidden until something new happens', marksRead ? { logged: false } : { logged: false, undo: () => void restore(it.id) });
      ctx.reloadAttention();
    } catch (e) {
      setGone((g) => { const n = new Set(g); n.delete(it.id); return n; });
      ctx.toast((e as Error).message, '', { logged: false });
    }
  };
  const o = ov.data;
  const db = o?.db.value ?? null;
  const dbAsleep = !!o?.db.asleep;
  const apiErr = describeErr(ov.error, 'The API');
  const running = jobs.data?.jobs.find((j) => j.state === 'running') ?? null;
  const dbAsOf = useMemo(() => (ov.at ? hhmmss(ov.at - (o?.db.ageS ?? 0) * 1000) : null), [ov.at, o?.db.ageS]);

  const apps = (['engine', 'engine-en', 'api', 'app'] as AppKey[]).map((k) => ({ k, a: o?.apps[k] }));
  const current = o?.image ? shortDigest(o.image.split('@')[1]) : null;

  const fin = db?.queues.finalize_jobs;
  const mx = o?.metrics ?? null;

  const appRows: Row[] = apps.map(({ k, a }) => {
    const go = () => ctx.go(({ engine: 'engines', 'engine-en': 'engines', api: 'api', app: 'releases' } as const)[k]);
    if (!a) return { key: k, cells: [{ t: flyApp(env, k), sub: ROLE[k], mono: true }, { t: '—' }, { t: '—' }, { pill: { label: 'NO DATA', tone: 'mute' } }], onClick: go };
    if (isErr(a)) return { key: k, cells: [{ t: a.name, sub: ROLE[k], mono: true }, { t: 'Fly did not answer', sub: a.error.slice(0, 120), fg: 'var(--conf-low)' }, { t: '—' }, { pill: { label: 'UNREACHABLE', tone: 'bad' } }], onClick: go };
    const status: { label: string; tone: Tone } = a.waitingToUpdate.length ? { label: `${a.waitingToUpdate.length} WAITING`, tone: 'warn' } : a.images.length > 1 ? { label: 'DRIFT', tone: 'warn' } : a.behind.length ? { label: 'BEHIND', tone: 'warn' } : { label: 'IN SYNC', tone: 'ok' };
    return {
      key: k, onClick: go,
      cells: [
        { t: a.name, sub: ROLE[k], mono: true },
        { t: `${a.started} started · ${a.stopped} stopped · ${a.suspended} suspended`, sub: pl(a.machines, 'machine') },
        { t: a.images.map((i) => shortDigest(i)).join(', ') || '—', sub: a.images.length > 1 ? `${a.images.length} images` : current && a.images[0] && shortDigest(a.images[0]) === current ? 'the API app’s image' : '', mono: true },
        { pill: status, sub: a.waitingToUpdate.length ? `update when ${a.waitingToUpdate.length === 1 ? 'it stops' : 'they stop'}` : undefined },
      ],
    };
  });

  const att = ctx.attention;
  useEffect(() => { setGone(new Set()); }, [att]);
  const inbox = (att?.items ?? []).filter((i) => !gone.has(i.id));
  const counts = { action: inbox.filter((i) => i.severity === 'action').length, watch: inbox.filter((i) => i.severity === 'watch').length, info: inbox.filter((i) => i.severity === 'info').length };
  const shown = sev ? inbox.filter((i) => i.severity === sev) : inbox;
  const flyBlock = { asOf: hhmmss(ov.at), stale: stale(ov.ageS, 5), loading: ov.loading && !o, error: apiErr && !o ? { ...apiErr, retry: ov.reload } : null };
  const dbBlock = { asOf: dbAsOf, stale: !!o && o.db.ageS > 30, asleep: dbAsleep, onWake: () => { setWake(true); ov.reload(); }, loading: ov.loading && !o, error: apiErr && !o ? { ...apiErr, retry: ov.reload } : null };

  return (
    <>
      <Head title="Inbox" sub={`What needs you in ${env.label}, most urgent first; apps, queues and the database below. Refreshed every 30 s.`} />
      <Grid>
        <Cell span={12}>
          <Block title="Needs you" count={inbox.length ? `${counts.action} action · ${counts.watch} watch` : undefined}
            asOf={hhmmss(ctx.attentionAt)} stale={!!att && att.ageS > 60} asleep={!!att?.asleep} onWake={ctx.reloadAttention}
            loading={!att} error={att?.error ? { title: 'The Inbox could not be read', text: att.error, retry: () => ctx.reloadAttention() } : null}
            chips={[
              { label: `All · ${inbox.length}`, on: sev === '', onClick: () => setSev('') },
              { label: `Action · ${counts.action}`, on: sev === 'action', onClick: () => setSev(sev === 'action' ? '' : 'action') },
              { label: `Watch · ${counts.watch}`, on: sev === 'watch', onClick: () => setSev(sev === 'watch' ? '' : 'watch') },
              { label: `Info · ${counts.info}`, on: sev === 'info', onClick: () => setSev(sev === 'info' ? '' : 'info') },
            ]}
            empty={att && shown.length === 0 ? (sev ? 'Nothing at this level.' : 'Nothing needs you right now.') : undefined} emptyGood={!sev}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {shown.map((it) => {
                const s = SEVERITY[it.severity];
                return (
                  <div key={it.id} className="inbox__item">
                    <button type="button" className="inbox__row" onClick={() => it.link.screen && ctx.open(it.link.screen, it.link.drawer, it.link.params)}>
                      <span className="inbox__sev" style={{ background: s.fg }} />
                      <Pill tone={s.tone}>{s.label}</Pill>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-96)', textWrap: 'pretty' }}>{it.title}</div>
                        {it.detail && <div style={{ fontSize: 12, fontWeight: 300, color: 'var(--ink-62)', marginTop: 2, textWrap: 'pretty' }}>{it.detail}</div>}
                      </div>
                      {it.at && <span className="mono" title={fmtDate(it.at)} style={{ flex: 'none', fontSize: 11, color: 'var(--ink-52)', whiteSpace: 'nowrap' }}>{ago(it.at)}</span>}
                    </button>
                    <div className={`inbox__acts ${snoozing === it.id ? 'inbox__acts--on' : ''}`}>
                      {snoozing === it.id ? (
                        <>
                          {SNOOZE.map(([label, h]) => <button key={h} type="button" className="btn btn--sm" onClick={() => void dismiss(it, h)}>{label}</button>)}
                          <button type="button" className="btn btn--sm" onClick={() => setSnoozing(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="btn btn--sm" title={it.kind === 'notification' ? 'Mark these notifications read' : 'Hide until something new happens: a newer failure or a higher count brings it back'} onClick={() => void dismiss(it)}>{it.kind === 'notification' ? 'Mark read' : 'Done'}</button>
                          <button type="button" className="btn btn--sm" title="Hide for a while, whatever happens" onClick={() => setSnoozing(it.id)}>Snooze</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {(att?.hidden ?? 0) > 0 && (
                <div style={{ padding: '8px 14px', borderTop: '1px solid var(--hair)', fontSize: 12, color: 'var(--ink-52)' }}>
                  <button type="button" className="btn btn--link" onClick={() => (hiddenList ? setHiddenList(null) : void showHidden())}>{pl(att!.hidden!, 'item')} done or snoozed{hiddenList ? ' · hide the list' : ' · show'}</button>
                  {hiddenList && hiddenList.map((d) => (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                      <span style={{ flex: 1, minWidth: 0, color: 'var(--ink-78)' }}>{d.title}<span style={{ color: 'var(--ink-45)' }}> · {d.until ? `snoozed until ${fmtDate(d.until)}` : 'done'} by {d.by}</span></span>
                      <button type="button" className="btn btn--sm" onClick={() => void restore(d.id)}>Bring back</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Block>
        </Cell>
        {running && engines.data && <Cell span={12}><JobBanner job={running} machines={engines.data.machines} ctx={ctx} /></Cell>}

        <Cell span={12}>
          <Block title="Platform" {...dbBlock} empty={o && !db ? `${env.key === 'production' ? 'Neon' : 'The database'} is idle and nothing is cached yet. Wake the database to read the counts.` : undefined}>
            <Kpis min={mobile ? 120 : 150} items={[
              { label: 'LIVE RECORDINGS', v: db?.liveRecordings ?? 0, sub: `on ${pl(db?.liveRecordings ?? 0, 'engine')}`, dot: db?.liveRecordings ? 'live' : undefined, onClick: () => ctx.go('engines') },
              { label: 'OPEN RECORDINGS', v: db?.openRecordings ?? 0, sub: 'live, paused or pending' },
              { label: 'LISTENERS', v: o?.listeners ?? 0, sub: 'subscribed to this API app' },
              { label: 'FINALIZE', v: fin ? `${fin.open} · ${fin.failed}` : '—', sub: 'open · failed', fg: fin?.failed ? 'var(--conf-low)' : undefined, onClick: () => ctx.go('jobs', { tab: 'finalize' }) },
              { label: 'PUBLISH FAILURES', v: mx ? mx.publishFailures : '—', sub: mx ? 'vs_publish_failures_total' : 'metrics skipped while idle', fg: mx?.publishFailures ? 'var(--conf-mid)' : undefined, onClick: () => ctx.go('api') },
              { label: 'TRANSLATION', v: mx ? `${mx.translationErrors} · ${mx.provider429s}` : '—', sub: mx ? 'translation_errors · provider_429s' : 'metrics skipped while idle', fg: mx?.translationErrors ? 'var(--conf-mid)' : undefined, onClick: () => ctx.go('api') },
            ]} />
          </Block>
        </Cell>

        <Cell span={12}>
          <Block title="Apps" sub={`Machines against the image ${env.label}'s API app runs`} {...flyBlock}>
            <Table mobile={mobile} cols={[{ label: 'APP', width: 'minmax(150px,1.2fr)' }, { label: 'MACHINES', width: 'minmax(180px,1.4fr)' }, { label: 'IMAGE', width: 'minmax(150px,1.1fr)' }, { label: 'STATUS', width: 'minmax(110px,.8fr)' }]} rows={appRows} />
          </Block>
        </Cell>
        <Cell span={mobile ? 12 : 7}>
          <Block title="Job queues" sub="Open jobs per table" {...dbBlock} empty={o && !db ? 'No cached counts.' : undefined}>
            <Kpis min={120} items={QUEUES.filter(([t]) => db?.queues[t]).map(([t, label, tab]) => { const q = db!.queues[t]; return { label, v: q.open + q.running, sub: q.failed ? `${q.failed} failed` : 'none failed', dot: q.failed ? ('bad' as Tone) : undefined, onClick: () => ctx.go('jobs', { tab }) }; })} />
          </Block>
        </Cell>
        <Cell span={mobile ? 12 : 5}>
          <Block title="Database" {...dbBlock}>
            <Kv kw="44%" rows={[
              { k: env.key === 'production' ? 'Neon' : 'Postgres', v: o ? (dbAsleep || !o.awake ? { pill: { label: 'IDLE', tone: 'mute' }, t: 'scaled to zero' } : { pill: { label: 'AWAKE', tone: 'ok' } }) : { t: '—' }, acts: o && (dbAsleep || !o.awake) ? [{ label: 'Wake', onClick: () => { setWake(true); ov.reload(); } }] : undefined },
              { k: 'Pool', v: o ? { t: `${o.pool.size - o.pool.idle} busy of ${o.pool.size}`, sub: o.pool.max ? `max ${o.pool.max}` : `${o.pool.idle} idle` } : { t: '—' } },
              { k: 'Admin share', v: { t: 'not reported by the API', fg: 'var(--ink-55)' } },
              { k: 'Schema baseline', v: { t: db?.schema || db?.newestMigration || '—', mono: true } },
              { k: 'Usage rows pending', v: { t: String(o?.usagePending ?? '—'), sub: 'counted per minute, written in batches' } },
            ]} />
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title="Last 10 admin actions" {...dbBlock} headActs={[{ label: 'Audit log', onClick: () => ctx.go('audit') }]} empty={o && !o.staffActions.length ? 'No admin actions yet.' : undefined}>
            <List items={(o?.staffActions ?? []).map((a) => ({ key: a.id, t: `${a.actor || 'admin'} · ${a.action}`, sub: `${a.targetType} ${a.targetId}`, right: fmtDate(a.at), onClick: () => ctx.go('audit', { id: a.id }) }))} />
          </Block>
        </Cell>

      </Grid>
    </>
  );
}
